"use client";

/**
 * Focused Schedule View
 *
 * Full-page, focused calendar for the public landing page schedule. Rendered via the page's
 * `?view=schedule` query param (opened in its own browser tab) so attendees get a distraction-free,
 * Google-Calendar-style time grid that fills the viewport on both desktop and mobile.
 *
 * Reads the same shared schedule data the inline section uses (SchedulePublicDataProvider), so it
 * stays in sync with the page. Always renders the calendar time grid regardless of the section's
 * configured display style, and reuses CalendarView (pinch-zoom, now-line, registration pills) plus
 * ScheduleDetailModal (signup flow) unchanged — only the grid height is grown to fill the screen.
 */

import type React from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { ArrowLeft } from "lucide-react";

import { useBuilderContext } from "../../_hooks/useBuilderContext";
import { useViewerRegistrations } from "../../_hooks/useViewerRegistrations";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../_logic/types/db-types";
import { groupItemsByDate, isTimedItem } from "../../_logic/utils/schedule-calendar-layout";
import { AddToCalendarLink } from "../canvas/renderers/schedule/AddToCalendarLink";
import { CalendarView } from "../canvas/renderers/schedule/CalendarView";
import { ScheduleLabelFilter } from "../canvas/renderers/schedule/ScheduleLabelFilter";
import { useSchedulePublicData } from "./SchedulePublicDataProvider";

// The detail/signup modal only mounts on item click — load it on demand so its form/validation deps
// stay out of this route's initial bundle.
const ScheduleDetailModal = dynamic(
  () => import("../canvas/renderers/schedule/ScheduleDetailModal").then((m) => m.ScheduleDetailModal),
  { ssr: false }
);

interface FocusedScheduleViewProps {
  /** Display name of the landing page, shown as the eyebrow above the heading. */
  pageName: string;
  /** Href back to the landing page this schedule belongs to. */
  backHref: string;
}

/** Smallest focused-calendar viewport (px) allowed after the compact toolbar is measured. */
const MIN_CALENDAR_VIEWPORT_PX = 320;

/** First-paint fallback before the measured viewport is available. */
const FOCUSED_SCHEDULE_FALLBACK_VIEWPORT_PX = 640;

/** Stable empty fallbacks so derived memos don't bust on a momentarily-null data context. */
const EMPTY_ITEMS: ScheduleItemRow[] = [];
const EMPTY_LABELS: ScheduleLabelRow[] = [];

/**
 * Renders the focused full-page calendar.
 *
 * @param props - See FocusedScheduleViewProps.
 * @returns The focused schedule page render.
 */
export function FocusedScheduleView({ pageName, backHref }: FocusedScheduleViewProps): React.ReactElement {
  // Hooks
  const { primaryColor, timeZone } = useBuilderContext();
  const publicData = useSchedulePublicData();
  const viewerRegistrations = useViewerRegistrations();
  const headerRef = useRef<HTMLElement | null>(null);

  // States
  const [selectedItem, setSelectedItem] = useState<ScheduleItemRow | null>(null);
  const [activeLabelId, setActiveLabelId] = useState<number | null>(null);
  const [calendarViewportPx, setCalendarViewportPx] = useState<number>(FOCUSED_SCHEDULE_FALLBACK_VIEWPORT_PX);

  // Derived values
  const items = publicData?.items ?? EMPTY_ITEMS;
  const labels = publicData?.labels ?? EMPTY_LABELS;
  const isLoading = publicData?.isLoading ?? true;
  const publicCalendarUrl = publicData?.publicCalendarUrl ?? null;
  const showTimeLabels = publicData?.config?.show_time_labels ?? true;
  const labelMap = useMemo(() => new Map(labels.map((l) => [l.id, l])), [labels]);

  const filteredItems = useMemo(() => {
    if (!activeLabelId) return items;
    return items.filter((item) => item.label_ids.includes(activeLabelId));
  }, [items, activeLabelId]);
  const dateGroups = useMemo(() => groupItemsByDate(filteredItems), [filteredItems]);
  const hasItems = items.length > 0;
  const hasTimedItems = useMemo(() => items.some(isTimedItem), [items]);

  // Same measured height feeds the desktop cap and the mobile viewport sizing math.
  const gridViewport = useMemo(
    () => ({ desktopMaxHeight: calendarViewportPx, mobileViewportPx: calendarViewportPx, fillAvailable: true }),
    [calendarViewportPx]
  );
  const backLabel = pageName ? `Back to ${pageName}` : "Back to page";

  // Effects — size the calendar area from the live viewport minus the measured toolbar. The page
  // itself is fixed-height/overflow-hidden; the calendar grid owns vertical scrolling on mobile.
  useLayoutEffect(() => {
    let raf = 0;
    const getViewportHeight = (): number => window.visualViewport?.height ?? window.innerHeight;
    const apply = (): void =>
      setCalendarViewportPx((prev) => {
        const headerHeight = headerRef.current?.offsetHeight ?? 0;
        const next = Math.max(MIN_CALENDAR_VIEWPORT_PX, getViewportHeight() - headerHeight);
        return next === prev ? prev : next;
      });
    apply();
    const onResize = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };

    const header = headerRef.current;
    const observer =
      header && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(apply);
          })
        : null;
    if (header && observer) observer.observe(header);
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("scroll", onResize);
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("scroll", onResize);
    };
  }, []);

  // Effects — this utility route is meant to behave like a fixed app surface. Lock the document
  // while it is mounted so rubber-band/body scroll cannot move the focused chrome behind the grid.
  useLayoutEffect(() => {
    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyOverscroll = body.style.overscrollBehavior;
    const previousHtmlOverflow = documentElement.style.overflow;
    const previousHtmlOverscroll = documentElement.style.overscrollBehavior;

    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    documentElement.style.overflow = "hidden";
    documentElement.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow = previousBodyOverflow;
      body.style.overscrollBehavior = previousBodyOverscroll;
      documentElement.style.overflow = previousHtmlOverflow;
      documentElement.style.overscrollBehavior = previousHtmlOverscroll;
    };
  }, []);

  // Render
  return (
    <div className="flex h-[100dvh] min-h-0 flex-col overflow-hidden overscroll-none bg-white text-gray-900">
      {/* Compact top bar — the page itself doesn't scroll, so this stays fixed without sticky layout. */}
      <header ref={headerRef} className="shrink-0 border-b border-gray-200 bg-white">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-1.5 px-3 py-2 sm:px-4">
          <div className="flex min-h-9 min-w-0 items-center gap-1.5">
            <a
              href={backHref}
              aria-label={backLabel}
              className="inline-flex h-9 w-9 shrink-0 touch-manipulation items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-1"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{backLabel}</span>
            </a>
            <div className="min-w-0 flex-1">
              {pageName && <p className="truncate text-xs font-medium leading-4 text-gray-500">{pageName}</p>}
              <h1 className="truncate text-base font-semibold leading-5 text-gray-900">Schedule</h1>
            </div>
            {publicCalendarUrl && (
              <AddToCalendarLink href={publicCalendarUrl} className="ml-auto shrink-0 px-2.5 py-1.5 text-xs" />
            )}
          </div>

          <ScheduleLabelFilter
            labels={labels}
            activeLabelId={activeLabelId}
            onChange={setActiveLabelId}
            primaryColor={primaryColor}
            compact
            scrollable
            className="-mx-3 px-3 sm:-mx-4 sm:px-4"
          />
        </div>
      </header>

      {/* Calendar */}
      <main className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col overflow-hidden sm:px-4 sm:pb-4">
        {isLoading ? (
          <div className="flex flex-col gap-3 p-3 sm:p-0 sm:pt-4" aria-busy="true">
            <span className="sr-only" role="status">
              Loading schedule…
            </span>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex animate-pulse gap-3 rounded-lg bg-gray-100 p-4">
                <div className="h-4 w-20 rounded bg-gray-200" />
                <div className="flex-1">
                  <div className="h-4 w-3/4 rounded bg-gray-200" />
                  <div className="mt-2 h-3 w-1/2 rounded bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        ) : !hasItems ? (
          <p className="flex min-h-0 flex-1 items-center justify-center p-6 text-center font-medium text-gray-500">
            No schedule items yet.
          </p>
        ) : filteredItems.length === 0 ? (
          <p
            role="status"
            className="flex min-h-0 flex-1 items-center justify-center p-6 text-center font-medium text-gray-500"
          >
            No items match this filter.
          </p>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col pt-3 sm:pt-4">
            <CalendarView
              dateGroups={dateGroups}
              labelMap={labelMap}
              primaryColor={primaryColor}
              onItemClick={setSelectedItem}
              timeZone={timeZone}
              isPublic
              viewerRegistrations={viewerRegistrations}
              viewport={gridViewport}
              showTimeLabels={showTimeLabels}
            />
          </div>
        )}

        {/* When the schedule has only undated items, the heading still reads "Schedule" and
            CalendarView renders just the Unscheduled section — no extra messaging needed. */}
        {!isLoading && hasItems && !hasTimedItems && (
          <p className="shrink-0 p-3 text-center text-sm font-medium text-gray-500">
            This schedule has no timed items yet.
          </p>
        )}
      </main>

      {/* Detail / signup modal */}
      {selectedItem && (
        <ScheduleDetailModal
          item={selectedItem}
          labels={labels}
          primaryColor={primaryColor}
          showTimeLabels={showTimeLabels}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}
